import React, { useState } from 'react';
import { HelpCircle, Plus, Minus } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: 'What is a container desiccant and why do exporters need it?',
      answer:
        'A container desiccant is a moisture-absorbing product hung inside shipping containers to prevent container rain. NOMOISTURE calcium chloride desiccant bags absorb humidity and condensation caused by temperature swings during ocean freight, protecting export cargo from mold, corrosion and moisture damage.',
    },
    {
      question: 'How is calcium chloride better than silica gel for shipping containers?',
      answer:
        'High-absorption calcium chloride can hold up to 300% of its own weight in water, far more than traditional silica gel. That makes NOMOISTURE calcium chloride desiccant bags the preferred choice for large-volume export cargo humidity protection. Silica gel pouches are ideal for smaller boxed products, electronics and pharmaceuticals.',
    },
    {
      question: 'What is container rain and how do NOMOISTURE desiccants prevent it?',
      answer:
        'Container rain is condensation that forms on the ceiling and walls of a shipping container when warm humid air cools during ocean freight. NOMOISTURE desiccants continuously absorb airborne moisture, keeping relative humidity low so container rain, mold and rust never damage your export packaging or cargo.',
    },
    {
      question: 'Which industries use NOMOISTURE desiccants?',
      answer:
        'NOMOISTURE desiccants are used by exporters and manufacturers in food and agro products, pharmaceuticals, textiles, leather, electronics, FMCG, machinery and auto parts for humidity control, export cargo moisture protection and moisture damage control across storage and shipping.',
    },
    {
      question: 'How many NOMOISTURE desiccant bags do I need per 20ft or 40ft container?',
      answer:
        'As a rule of thumb, use 6–8 NOMOISTURE calcium chloride desiccant bags per 20ft container and 10–16 bags per 40ft container, depending on cargo, packaging and voyage duration. Contact our team for a tailored recommendation for your export packaging.',
    },
    {
      question: 'Are NOMOISTURE silica gel pouches food-grade?',
      answer:
        'Yes. NOMOISTURE silica gel pouches are available in industrial and food-contact compatible grades, making them safe for use with food, nutraceuticals and pharmaceutical packaging for humidity control and moisture damage control.',
    },
  ];

  const toggle = (idx) => {
    setOpenIndex(openIndex === idx ? -1 : idx);
  };

  return (
    <section
      id="faq"
      aria-labelledby="faq-heading"
      style={{
        background: '#0a0e1a',
        padding: 'clamp(3rem, 8vw, 5rem) 1.5rem',
      }}
    >
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(236, 236, 117, 0.15)',
              border: '1px solid rgba(236, 236, 117, 0.3)',
              padding: '0.5rem 1.5rem',
              borderRadius: '50px',
              marginBottom: '1.5rem',
            }}
          >
            <HelpCircle size={20} style={{ color: '#ECEC75' }} />
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#ECEC75' }}>
              Frequently Asked Questions
            </span>
          </div>

          <h2
            id="faq-heading"
            style={{
              fontFamily: "'Crimson Text', serif",
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 700,
              color: '#f1f5f9',
              marginBottom: '1rem',
            }}
          >
            NOMOISTURE Container Desiccant FAQs
          </h2>

          <p
            style={{
              fontSize: 'clamp(1rem, 2vw, 1.125rem)',
              color: '#cbd5e1',
              maxWidth: '720px',
              margin: '0 auto',
              lineHeight: 1.8,
            }}
          >
            Answers to the most common questions about calcium chloride desiccant bags,
            silica gel pouches, container rain and export cargo humidity protection.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {faqs.map((item, idx) => {
            const isOpen = openIndex === idx;
            return (
              <article
                key={idx}
                itemScope
                itemType="https://schema.org/Question"
                style={{
                  background: '#121827',
                  border: '1px solid rgba(236, 236, 117, 0.15)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                }}
              >
                <h3 itemProp="name" style={{ margin: 0 }}>
                  <button
                    onClick={() => toggle(idx)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${idx}`}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1.25rem 1.5rem',
                      background: 'transparent',
                      border: 'none',
                      color: '#f1f5f9',
                      fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                      fontWeight: 600,
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span>{item.question}</span>
                    <span
                      style={{
                        flexShrink: 0,
                        color: '#ECEC75',
                        background: 'rgba(236, 236, 117, 0.15)',
                        border: '1px solid rgba(236, 236, 117, 0.3)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                    </span>
                  </button>
                </h3>
                <div
                  id={`faq-answer-${idx}`}
                  role="region"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  style={{
                    maxHeight: isOpen ? '600px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease',
                  }}
                >
                  <p
                    itemProp="text"
                    style={{
                      margin: 0,
                      padding: '0 1.5rem 1.25rem',
                      color: '#cbd5e1',
                      fontSize: '1rem',
                      lineHeight: 1.8,
                    }}
                  >
                    {item.answer}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
